import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminECalculator.css';
import '../../styles/components/ainutritionlandingpage.css';


// 2. α-Tocopherol Equivalent (α-TE) conversions (Focus on alpha for simplicity based on DRI/UL)
const alphaTEConversionFactors = {
    natural_alpha: 1.0,  // 1 mg RRR-α-tocopherol -> 1 mg α-TE
    synthetic_alpha: 0.5, // 1 mg all-rac-α-tocopherol -> 0.5 mg α-TE
    // Factors for beta, gamma, delta are less commonly used in basic RDA calculations
    // but could be added for a more complex mixed tocopherol calculation if needed.
};
// Convert IU to mg for user reference (Approximate)
// 1 IU natural ≈ 0.67 mg α-TE
// 1 IU synthetic ≈ 0.45 mg α-TE

// 3. Reference Intakes (DRIs, mg α-TE/day)
const vitERdaTable = { // mg α-TE/day
    '0-6m': { target: 4, isAI: true },
    '7-12m': { target: 5, isAI: true },
    '1-3y': { target: 6 },
    '4-8y': { target: 7 },
    '9-13y': { target: 11 },
    '14+y': { target: 15 }, // Combined M/F as RDA is the same
    'pregnant': { target: 15 },
    'lactating': { target: 19 },
};
const vitE_UL_mg = 1000; // Adult UL for α-tocopherol supplements (natural or synthetic combined after conversion factor?) - ODS states 1000mg for any form of supplemental alpha-tocopherol.

// Absorption Penalty
const FAT_MALABSORPTION_PENALTY = 0.20; // -20% absorption

// Safety Thresholds
const HIGH_DOSE_WARN_MG = 300; // Warn if total alpha-tocopherol > 300mg/day, especially with anticoagulants
const OMEGA3_SYNERGY_THRESHOLD_MG = 2000; // mg EPA+DHA
const OMEGA3_VITAMIN_E_RATIO = 0.6; // mg E per gram (1000mg) Omega-3

// 6. Food Composition Snippets (mg α-tocopherol per typical serve)
const foodVitEValues = { // Store mg of natural alpha-tocopherol per serving
    sunflower_seeds_1oz: { alpha: 7.4 }, // 28g = 1 oz
    almonds_1oz: { alpha: 7.3 },
    wheat_germ_oil_tbsp: { alpha: 20.3 }, // High source
    hazelnuts_1oz: { alpha: 4.3 },
    peanut_butter_2tbsp: { alpha: 2.9 },
    peanuts_1oz: { alpha: 2.4 },
    avocado_half: { alpha: 2.1 },
    spinach_cooked_half_cup: { alpha: 1.9 },
    broccoli_cooked_half_cup: { alpha: 1.2 },
    olive_oil_tbsp: { alpha: 1.9 },
    canola_oil_tbsp: { alpha: 1.8 },
    sunflower_oil_tbsp: { alpha: 5.6 },
    soybean_oil_tbsp: { alpha: 0.8 }, // Lower in alpha, higher in gamma
    // Placeholders for foods higher in other tocopherols if needed later
    // corn_oil_tbsp: { alpha: 1.9, gamma: ~6? },
};


// --- Helper Functions ---

const getVitERdaKey = (ageNum, isPregnant, isLactating) => {
    if (ageNum < 0.5) return '0-6m';
    if (ageNum < 1) return '7-12m';
    if (ageNum <= 3) return '1-3y';
    if (ageNum <= 8) return '4-8y';
    if (ageNum <= 13) return '9-13y';
    if (ageNum >= 14) {
        if (isLactating) return 'lactating';
        if (isPregnant) return 'pregnant';
        return '14+y';
    }
    return '14+y'; // Default fallback
};


// --- Component ---
function VitaminECalculator() { // <<< RENAMED Component
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // Still needed for context/disabling, even if RDA same for M/F 14+
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);

    // Diet Diary (Simplified Daily Servings)
    const [dietServings, setDietServings] = useState({
        servings_nuts_seeds: '', // e.g., 1oz sunflower seeds or almonds
        servings_veg_oil: '', // tbsp used daily (specify type below?)
        servings_avocado: '', // halves
        servings_greens: '', // cooked half cups (spinach, broccoli)
        servings_peanut_butter: '', // 2 tbsp servings
        servings_whole_grains: '', // Placeholder - generally low source unless wheat germ
    });
    const [cookingOilType, setCookingOilType] = useState('olive'); // Default, affects primary oil source calculation

    // Supplement Use
    const [takesSupplement, setTakesSupplement] = useState(false);
    const [supplementMgAlpha, setSupplementMgAlpha] = useState(''); // Total mg Alpha-Tocopherol per day
    const [supplementForm, setSupplementForm] = useState('natural'); // 'natural' (d-alpha), 'synthetic' (dl-alpha)

    // Health & Lifestyle Flags
    const [hasFatMalabsorption, setHasFatMalabsorption] = useState(false);
    const [isSmoker, setIsSmoker] = useState(false);
    const [isHeavyExerciser, setIsHeavyExerciser] = useState(false); // Endurance athlete flag
    const [isOnAnticoagulant, setIsOnAnticoagulant] = useState(false); // Warfarin or DOAC

    // Omega-3 Intake
    const [omega3DoseMg, setOmega3DoseMg] = useState(''); // Total EPA+DHA mg per day

    // Optional Lab Input
    const [serumAlphaTocopherol, setSerumAlphaTocopherol] = useState(''); // µmol/L

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null);
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleDietChange = (event) => {
        const { name, value } = event.target;
        setDietServings(prev => ({ ...prev, [name]: value }));
    };
    // Add handlers for checkboxes/selects as needed...


    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isFemale = sex === 'female';
    const highOxidationLoad = isSmoker || isHeavyExerciser;

    // Disable pregnancy/lactation based on sex and age
    useEffect(() => {
        if (sex === 'male' || ageNum < 12 || ageNum > 55) {
            setIsPregnant(false);
            setIsLactating(false);
        }
    }, [sex, ageNum]);


    // --- Calculation Logic ---
    const calculateVitaminEStatus = (e) => {
        e.preventDefault();
        setShowResults(false);
        setCalculationResult(null);

        let explanation = [];
        let safetyFlags = [];
        let suggestions = { food: [], supplement: [], lifestyle: [], general: [] };
        let target_aTE = 0;

        // Validate essential inputs
        if (isNaN(ageNum) || !sex) {
            alert("Please fill in required fields: Age and Sex.");
            return;
        }

        // --- 1. Target Setting ---
        const rdaKey = getVitERdaKey(ageNum, isPregnant, isLactating);
        target_aTE = vitERdaTable[rdaKey]?.target || 15; // Fallback to adult RDA
        const targetIsAI = vitERdaTable[rdaKey]?.isAI || false;
        explanation.push(`Base Vitamin E ${targetIsAI ? 'AI' : 'RDA'} Target (${rdaKey.replace('_', ' ')}): ${target_aTE} mg α-TE/day.`);

        // Optional boost for high oxidation load
        const targetBoost = highOxidationLoad ? 2 : 0;
        if (targetBoost > 0) {
            target_aTE += targetBoost;
            explanation.push(`Target optionally increased by ${targetBoost} mg (to ${target_aTE} mg α-TE/day) due to smoking/heavy exercise status.`);
            suggestions.lifestyle.push("Your smoking status or heavy exercise may increase antioxidant needs. Aiming for the upper end of normal intake or slightly above the RDA can be beneficial.");
        }

        // --- 2. Intake (Calculate α-TE) ---
        let food_alpha_mg = 0; // Store raw alpha-tocopherol mg from food

        const getDietVal = (key) => parseFloat(dietServings[key]) || 0;

        // Estimate food alpha-tocopherol (mg)
        // Note: This is highly simplified. A real app needs a detailed food log.
        food_alpha_mg += getDietVal('servings_nuts_seeds') * foodVitEValues.almonds_1oz.alpha; // Using almonds as representative
        food_alpha_mg += getDietVal('servings_avocado') * foodVitEValues.avocado_half.alpha;
        food_alpha_mg += getDietVal('servings_greens') * foodVitEValues.spinach_cooked_half_cup.alpha; // Using spinach as representative
        food_alpha_mg += getDietVal('servings_peanut_butter') * foodVitEValues.peanut_butter_2tbsp.alpha;

        // Add oil contribution based on type
        const oilServings = getDietVal('servings_veg_oil');
        if (oilServings > 0) {
            const oilAlphaMg = foodVitEValues[`${cookingOilType}_oil_tbsp`]?.alpha || foodVitEValues.olive_oil_tbsp.alpha; // Fallback to olive oil
            food_alpha_mg += oilServings * oilAlphaMg;
            explanation.push(`Estimated contribution from ${oilServings.toFixed(1)} tbsp ${cookingOilType} oil: ${(oilServings * oilAlphaMg).toFixed(1)} mg α-tocopherol.`);
        }
        // Placeholder for whole grains - contribution usually small unless wheat germ
        // food_alpha_mg += getDietVal('servings_whole_grains') * (some small value);


        explanation.push(`Estimated α-tocopherol from food: ${food_alpha_mg.toFixed(1)} mg.`);
        // Since food sources are natural (RRR), food_alpha_mg is already in α-TE for the alpha component.
        const food_alpha_TE = food_alpha_mg * alphaTEConversionFactors.natural_alpha;


        // Calculate α-TE from Supplements
        let supp_alpha_TE = 0;
        const supp_alpha_mg_val = parseFloat(supplementMgAlpha) || 0;
        if (takesSupplement && supp_alpha_mg_val > 0) {
            const conversionFactor = supplementForm === 'synthetic' ? alphaTEConversionFactors.synthetic_alpha : alphaTEConversionFactors.natural_alpha;
            supp_alpha_TE = supp_alpha_mg_val * conversionFactor;
            explanation.push(`Supplement contribution: ${supp_alpha_mg_val.toFixed(0)} mg α-tocopherol (${supplementForm}) = ${supp_alpha_TE.toFixed(1)} mg α-TE.`);
        }

        // Total α-TE (Simplified - assumes only alpha contributes significantly or is tracked)
        // A more complex calc would add TE from food gamma/beta/delta if data available
        const total_alpha_TE = food_alpha_TE + supp_alpha_TE;
        explanation.push(`Total Estimated Intake (before absorption adj.): ${total_alpha_TE.toFixed(1)} mg α-TE/day.`);

        // --- 3. Absorption Adjustment ---
        const abs_factor = hasFatMalabsorption ? (1.0 - FAT_MALABSORPTION_PENALTY) : 1.0;
        const effective_TE = total_alpha_TE * abs_factor;

        if (hasFatMalabsorption) {
            explanation.push(`Absorption Factor applied: ${abs_factor.toFixed(2)}x due to malabsorption condition.`);
            safetyFlags.push({ type: 'info', text: `Fat malabsorption condition noted. Vitamin E absorption may be reduced by ~${FAT_MALABSORPTION_PENALTY * 100}%. Regular monitoring and specific guidance from your healthcare provider are recommended.` });
        }
        explanation.push(`==> FINAL Estimated Effective Intake: ${effective_TE.toFixed(1)} mg α-TE/day.`);

        // --- 4. Gap & Safety ---
        const gap = target_aTE - effective_TE;
        explanation.push(`Gap compared to Target: ${gap > 0 ? `${gap.toFixed(1)} mg α-TE/day deficit` : `Adequate / Surplus (${Math.abs(gap).toFixed(1)} mg α-TE).`}`);

        // Safety Checks (Based on total alpha-tocopherol intake, NOT necessarily TE for UL)
        // The UL is based on mg of supplemental alpha-tocopherol. Let's use total alpha intake for caution.
        const total_alpha_mg = food_alpha_mg + supp_alpha_mg_val; // Sum of raw alpha mg

        if (total_alpha_mg > vitE_UL_mg) {
            safetyFlags.push({ type: 'severe', text: `UPPER LIMIT EXCEEDED: Your total alpha-tocopherol intake (~${total_alpha_mg.toFixed(0)} mg/day) exceeds the Tolerable Upper Intake Level (UL) of ${vitE_UL_mg} mg/day. High doses increase bleeding risk. CONSULT YOUR DOCTOR.` });
        } else if (total_alpha_mg > HIGH_DOSE_WARN_MG) {
            safetyFlags.push({ type: 'warning', text: `High Vitamin E Dose: Your total alpha-tocopherol intake (~${total_alpha_mg.toFixed(0)} mg/day) is high (> ${HIGH_DOSE_WARN_MG} mg).` });
            // Add anticoagulant specific warning if flag is set AND dose is high
            if (isOnAnticoagulant) {
                safetyFlags.push({ type: 'severe', text: `ANTICOAGULANT INTERACTION RISK: High Vitamin E intake (> ${HIGH_DOSE_WARN_MG} mg) combined with anticoagulant medication significantly increases bleeding risk. Discuss Vitamin E dosage with your prescriber IMMEDIATELY.` });
            }
        } else if (isOnAnticoagulant && total_alpha_mg > 100) { // Lower threshold warning if on anticoagulants
            safetyFlags.push({ type: 'info', text: `Anticoagulant Use: You are taking an anticoagulant. While doses below ${HIGH_DOSE_WARN_MG} mg/day are generally considered safe, inform your doctor about your Vitamin E intake (~${total_alpha_mg.toFixed(0)} mg/day).` });
        }

        // Omega-3 Synergy Check
        const omega3Dose = parseFloat(omega3DoseMg) || 0;
        if (omega3Dose > OMEGA3_SYNERGY_THRESHOLD_MG) {
            const omega3Grams = omega3Dose / 1000;
            const recommendedVitE = omega3Grams * OMEGA3_VITAMIN_E_RATIO;
            suggestions.general.push(`Omega-3 Synergy: You reported taking ${omega3Dose} mg EPA+DHA. Higher omega-3 intake increases the need for antioxidants like Vitamin E to protect these fats. A general guideline is ~${OMEGA3_VITAMIN_E_RATIO} mg Vitamin E (α-TE) per gram of omega-3. Recommended E for your dose: ~${recommendedVitE.toFixed(1)} mg α-TE. Your current intake: ${effective_TE.toFixed(1)} mg α-TE.`);
            if (effective_TE < recommendedVitE * 0.8) { // If significantly below suggested ratio
                safetyFlags.push({ type: 'info', text: `Vitamin E intake may be low relative to high Omega-3 dose. Consider increasing Vitamin E rich foods or discuss supplementation balance with your provider.` });
            }
        }

        // Serum Level Context
        const serumVal = parseFloat(serumAlphaTocopherol);
        if (!isNaN(serumVal)) {
            explanation.push(`Reported Serum α-Tocopherol: ${serumVal.toFixed(1)} µmol/L.`);
            // Deficiency typically < 12 µmol/L
            if (serumVal < 12) {
                safetyFlags.push({ type: 'warning', text: `Low Serum α-Tocopherol Reported (${serumVal.toFixed(1)} µmol/L). This level (< 12 µmol/L) may indicate deficiency. Discuss this result and potential causes (diet, malabsorption) with your healthcare provider.` });
                suggestions.general.push("Your reported serum Vitamin E level is low. Follow up with your healthcare provider is strongly recommended.");
            } else if (serumVal > 70) { // Upper end of typical ranges, very high > 80-100?
                safetyFlags.push({ type: 'info', text: `Serum α-Tocopherol Reported (${serumVal.toFixed(1)} µmol/L) is in the upper range. Ensure total intake, especially from supplements, is within safe limits.` });
            }
            // Note: Serum levels can also be reported relative to lipids (e.g., mg/g total lipid). µmol/L is common but interpretation depends on lab reference ranges.
        }

        // --- Suggestions ---
        if (gap > 0) {
            suggestions.food.push(`You are estimated to be ${gap.toFixed(1)} mg α-TE short of your target.`);
            suggestions.food.push("Boost intake with Vitamin E rich foods: Nuts & Seeds (sunflower seeds: 7.4mg/oz, almonds: 7.3mg/oz), Vegetable Oils (wheat germ oil: 20mg/tbsp, sunflower oil: 5.6mg/tbsp), Avocado (2.1mg/half), Spinach (1.9mg/half cup cooked).");
            // Example specific suggestion:
            const almondTE = foodVitEValues.almonds_1oz.alpha; // ~7.3 mg α-TE
            if (gap >= almondTE * 0.8) {
                suggestions.food.push(`Tip: Adding just 1 ounce of almonds could provide ~${almondTE.toFixed(1)} mg α-TE, significantly closing the gap.`);
            }
        }

        // Supplement Suggestion (Conditional)
        if (gap >= 6 && !isOnAnticoagulant) { // Suggest if gap significant and no anticoagulant conflict
            suggestions.supplement.push(`Significant Gap (${gap.toFixed(1)} mg α-TE): If dietary changes are insufficient, discuss options with your provider. A supplement providing around 100-200 IU (approx 67-134 mg natural α-TE or less if synthetic) could be considered.`);
            // suggestions.supplement.push(`Example: [Link to Mixed Tocopherol 200 IU]`);
        } else if (gap >= 6 && isOnAnticoagulant) {
            suggestions.supplement.push(`Significant Gap (${gap.toFixed(1)} mg α-TE) noted, but supplementation requires caution due to anticoagulant use. Focus on food sources and discuss ANY supplement use with your doctor, even low doses.`);
        }

        // Set results state
        setCalculationResult({
            target_aTE: target_aTE.toFixed(1),
            effective_TE: effective_TE.toFixed(1),
            gap: gap.toFixed(1),
            totalAlphaTocopherolMg: total_alpha_mg.toFixed(1), // Raw alpha for safety check context
            foodAlphaTE: food_alpha_TE.toFixed(1),
            suppAlphaTE: supp_alpha_TE.toFixed(1),
            vitE_UL_mg: vitE_UL_mg,
            explanation: explanation.join('\n'),
            suggestions: suggestions,
            safetyFlags: safetyFlags,
        });
        setShowResults(true);
    };


    // --- JSX Structure ---
    return (
        <div className="vitaminECalculator">
            <h1>
                <span>
                    <span role="img" aria-label="vitamin E calculator">
                        🌻
                    </span>{' '}
                    Vitamin E (α-Tocopherol) Calculator
                </span>
                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This calculator estimates your Vitamin E intake as alpha-Tocopherol Equivalents (α-TE) based on common food values and your inputs. It is NOT a substitute for professional medical advice. Vitamin E needs can be influenced by diet (fat intake, PUFA levels), health status (malabsorption), and lifestyle (smoking, exercise). High doses of Vitamin E (especially &gt; 300 mg/day) can increase bleeding risk, particularly if taking anticoagulants (Warfarin, DOACs). The Tolerable Upper Intake Level (UL) for supplemental alpha-tocopherol is 1000 mg/day. Consult your healthcare provider regarding your Vitamin E status and needs.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateVitaminEStatus}> {/* <<< UPDATED HANDLER */}
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <h3>👤 Demographics</h3>
                    <label>
                        Age (Years):*
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 40" />
                    </label>
                    <label>
                        Sex Assigned at Birth:*
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {isFemale && ageNum > 11 && ageNum < 55 && ( // Show only if relevant
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating} />
                                Currently Pregnant?
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant} />
                                Currently Lactating?
                            </label>
                        </>
                    )}
                </div>

                {/* Diet Diary */}
                <div className="form-section">
                    <h3>🍽️ Estimated Daily Food Intake (Servings)</h3>
                    <p><small>Enter approximate number of servings PER DAY.</small></p>
                    <div className="diet-grid vit-e-diet-grid">
                        <label>Nuts/Seeds (1oz servings):<input type="number" step="0.5" name="servings_nuts_seeds" value={dietServings.servings_nuts_seeds} onChange={handleDietChange} min="0" placeholder="e.g., 1 (almonds/sunflower)" /></label>
                        <label>
                            Vegetable Oil (Tbsp):
                            <input type="number" step="0.5" name="servings_veg_oil" value={dietServings.servings_veg_oil} onChange={handleDietChange} min="0" placeholder="e.g., 2" />
                        </label>
                        <label>
                            Primary Cooking/Salad Oil:
                            <select value={cookingOilType} onChange={(e) => setCookingOilType(e.target.value)}>
                                <option value="olive">Olive Oil</option>
                                <option value="sunflower">Sunflower Oil</option>
                                <option value="canola">Canola Oil</option>
                                <option value="soybean">Soybean Oil</option>
                                <option value="wheat_germ">Wheat Germ Oil</option>
                                <option value="other">Other/Mixed</option> {/* Add other common ones */}
                            </select>
                        </label>
                        <label>Avocado (halves):<input type="number" step="0.5" name="servings_avocado" value={dietServings.servings_avocado} onChange={handleDietChange} min="0" placeholder="e.g., 0.5" /></label>
                        <label>Greens (cooked ½ cup):<input type="number" step="0.5" name="servings_greens" value={dietServings.servings_greens} onChange={handleDietChange} min="0" placeholder="e.g., 1 (spinach/broccoli)" /></label>
                        <label>Peanut Butter (2 Tbsp):<input type="number" step="0.5" name="servings_peanut_butter" value={dietServings.servings_peanut_butter} onChange={handleDietChange} min="0" placeholder="e.g., 1" /></label>
                        {/* <label>Whole Grains (servings):<input type="number" name="servings_whole_grains" value={dietServings.servings_whole_grains} onChange={handleDietChange} min="0" placeholder="e.g., 3" /></label> */}
                    </div>
                </div>

                {/* Supplements */}
                <div className="form-section">
                    <h3>💊 Supplement Use (Average Daily Intake)</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplement} onChange={(e) => setTakesSupplement(e.target.checked)} />
                        Do you take any supplements containing Vitamin E (alpha-tocopherol)?
                    </label>
                    {takesSupplement && (
                        <div className='supplement-details'>
                            <label>
                                Total Alpha-Tocopherol per day (mg):
                                <input type="number" value={supplementMgAlpha} onChange={(e) => setSupplementMgAlpha(e.target.value)} required={takesSupplement} min="0" placeholder="e.g., 134 (check label)" />
                                <small>Enter total MILLIGRAMS (mg) of alpha-tocopherol from all supplements.</small>
                            </label>
                            <label>
                                Form (Check Label):
                                <select value={supplementForm} onChange={(e) => setSupplementForm(e.target.value)} required={takesSupplement}>
                                    <option value="natural">Natural (d-alpha-tocopherol, RRR-)</option>
                                    <option value="synthetic">Synthetic (dl-alpha-tocopherol, all-rac-)</option>
                                    <option value="mixed">Mixed Tocopherols (Specify alpha amount above)</option>
                                    <option value="unknown">Unknown Form</option>
                                </select>
                                <small>Natural form has higher activity (1mg=1mg α-TE) than synthetic (1mg=0.5mg α-TE).</small>
                            </label>
                        </div>
                    )}
                </div>

                {/* Health & Lifestyle Flags */}
                <div className="form-section">
                    <h3>🩺 Health & Lifestyle Factors</h3>
                    <fieldset>
                        <legend>Check any that apply:</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" checked={hasFatMalabsorption} onChange={(e) => setHasFatMalabsorption(e.target.checked)} />
                            Condition affecting fat absorption? (Celiac, Crohn's, Pancreatitis, Bariatric Surgery)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" checked={isSmoker} onChange={(e) => setIsSmoker(e.target.checked)} />
                            Currently smoke cigarettes?
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" checked={isHeavyExerciser} onChange={(e) => setIsHeavyExerciser(e.target.checked)} />
                            Regularly engage in heavy endurance exercise? (&gt;1 hr intense daily)
                        </label>
                        <label className="checkbox-label indented alert-warning">
                            <input type="checkbox" checked={isOnAnticoagulant} onChange={(e) => setIsOnAnticoagulant(e.target.checked)} />
                            Taking anticoagulant medication? (Warfarin, Coumadin, Eliquis, Xarelto, Pradaxa, etc.)
                        </label>
                    </fieldset>
                </div>

                {/* Omega-3 Intake */}
                <div className="form-section">
                    <h3>🐟 Omega-3 Intake (Optional)</h3>
                    <label>
                        Estimated Daily Omega-3 (EPA + DHA) Intake from Supplements (mg):
                        <input type="number" value={omega3DoseMg} onChange={(e) => setOmega3DoseMg(e.target.value)} min="0" placeholder="e.g., 1000" />
                        <small>Enter total mg of EPA + DHA combined, if taking fish oil or algae oil supplements.</small>
                    </label>
                </div>

                {/* Optional Lab */}
                <div className="form-section">
                    <h3>🩸 Optional Lab Value (For Context)</h3>
                    <label>
                        Serum Alpha-Tocopherol (if known, µmol/L):
                        <input type="number" step="any" value={serumAlphaTocopherol} onChange={(e) => setSerumAlphaTocopherol(e.target.value)} placeholder="e.g., 25.5" />
                        <small>Deficiency typically &lt; 12 µmol/L. Consult lab reference range.</small>
                    </label>
                </div>


                <button type="submit">Calculate Vitamin E Status</button> {/* <<< UPDATED BUTTON TEXT */}
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Vitamin E (α-TE) Status</h2>

                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Important Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Gauge & Pie Chart Placeholder */}
                    <div className="result-gauge-placeholder dual-gauge vit-e-gauge">
                        <div className="gauge-pair">
                            <p>Effective α-TE vs Target</p>
                            <div className="gauge-bar" style={{ '--effective-value': calculationResult.effective_TE, '--target-value': calculationResult.target_aTE, '--max-value': Math.max(calculationResult.target_aTE * 1.5, calculationResult.effective_TE * 1.1, 20) }}>
                                <div className="gauge-fill"></div>
                                <div className="gauge-target-line"></div>
                            </div>
                            <span className="gauge-label">Est. Effective: {calculationResult.effective_TE} mg α-TE / Target: {calculationResult.target_aTE} mg α-TE</span>
                            <span className={`gauge-status status-${calculationResult.gap > 0 ? 'low' : 'adequate'}`}>
                                {calculationResult.gap > 0 ? `(~${calculationResult.gap} mg α-TE deficit)` : 'Adequate'}
                            </span>
                        </div>
                        {/* Pie Chart Placeholder */}
                        <div className="pie-chart-placeholder">
                            <p>α-TE Source Breakdown</p>
                            <div className="pie" style={{ '--p1': calculationResult.foodAlphaTE > 0 ? (calculationResult.foodAlphaTE / (calculationResult.foodAlphaTE + calculationResult.suppAlphaTE) * 100) : 0, '--p2': calculationResult.suppAlphaTE > 0 ? (calculationResult.suppAlphaTE / (calculationResult.foodAlphaTE + calculationResult.suppAlphaTE) * 100) : 0 }}>
                                <span>
                                    {calculationResult.foodAlphaTE > 0 || calculationResult.suppAlphaTE > 0 ?
                                        `${((calculationResult.foodAlphaTE / (calculationResult.foodAlphaTE + calculationResult.suppAlphaTE)) * 100).toFixed(0)}% Food` +
                                        ` / ${((calculationResult.suppAlphaTE / (calculationResult.foodAlphaTE + calculationResult.suppAlphaTE)) * 100).toFixed(0)}% Supp`
                                        : 'No Intake Entered'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>


                    <div className="results-grid">
                        <div className="result-item">
                            <span className="result-label">Target (RDA/AI):</span>
                            <span className="result-value">{calculationResult.target_aTE} mg α-TE/day</span>
                            <small className='result-note'>(Adjusted for lifestyle if applicable)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effective_TE} mg α-TE/day</span>
                            <small className='result-note'>(After absorption adjustment)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake vs Target:</span>
                            <span className={`result-value status-${calculationResult.gap > 0 ? 'low' : 'adequate'}`}>
                                {calculationResult.gap > 0 ? `Deficit (~${calculationResult.gap} mg α-TE)` : `Adequate / Surplus`}
                            </span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Food α-TE:</span>
                            <span className="result-value">{calculationResult.foodAlphaTE} mg α-TE</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Supplement α-TE:</span>
                            <span className="result-value">{calculationResult.suppAlphaTE} mg α-TE</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Total α-Tocopherol:</span>
                            <span className="result-value">{calculationResult.totalAlphaTocopherolMg} mg</span>
                            <small className='result-note'>(Check vs UL: {calculationResult.vitE_UL_mg} mg)</small>
                        </div>
                    </div>

                    <h3>💡 Guidance & Suggestions</h3>
                    {/* Gap Statement */}
                    <p className="gap-statement">
                        {calculationResult.gap > 0
                            ? `You appear to be about ${calculationResult.gap} mg α-TE short of your personalized daily target.`
                            : `Your estimated intake meets or exceeds your personalized daily target.`
                        }
                        {(calculationResult.totalAlphaTocopherolMg > HIGH_DOSE_WARN_MG) &&
                            <span className="alert-warning"> Note: Your total alpha-tocopherol intake is high (&gt; {HIGH_DOSE_WARN_MG} mg), increasing potential risks.</span>
                        }
                    </p>


                    {/* Suggestions Sections */}
                    {calculationResult.suggestions.food.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🍽️ Food Suggestions:</h4>
                            <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.lifestyle.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🌿 Lifestyle Considerations:</h4>
                            <ul>{calculationResult.suggestions.lifestyle.map((s, i) => <li key={`life-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.supplement.length > 0 && (
                        <div className="suggestions-box">
                            <h4>💊 Supplement Considerations (Discuss with Doctor FIRST):</h4>
                            <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
                            {/* Placeholder for affiliate links */}
                            {/* <p><small><i>Example supplement links could appear here.</i></small></p> */}
                        </div>
                    )}
                    {calculationResult.suggestions.general.length > 0 && (
                        <div className="suggestions-box">
                            <h4>⭐ General Notes & Synergy:</h4>
                            <ul>{calculationResult.suggestions.general.map((s, i) => <li key={`gen-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}


                    <h3>📝 Calculation Explanation</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <p className="important-note">
                        <strong>Reminder:</strong> This is an educational tool. α-TE (alpha-Tocopherol Equivalents) is used for RDA comparisons. Consult your healthcare provider for personalized advice, especially regarding supplements and potential drug interactions (like anticoagulants).
                    </p>
                    <a href="#vite-education" className="scroll-link">Learn more about Vitamin E below</a> {/* <<< UPDATED LINK HASH */}
                </div>
            )}

            {/* --- Educational Content (Vitamin E specific) --- */}
            <div id="vite-education" className="educational-section"> {/* <<< UPDATED ID */}
                <h2>Understanding Vitamin E</h2> {/* <<< UPDATED TITLE */}

                {/* Content Block 1: Basics */}
                <div className="content-block">
                    <h3>1. Vitamin E Basics</h3>
                    <dl>
                        <dt>Physiological Roles:</dt>
                        <dd>Vitamin E is a group of fat-soluble compounds, with alpha-tocopherol being the most biologically active form. Its primary role is as a <strong>chain-breaking antioxidant</strong>, protecting cell membranes (especially those rich in polyunsaturated fatty acids - PUFAs) and lipoproteins (like LDL cholesterol) from damage by free radicals. It also plays roles in immune function, cell signalling, and maintaining skin barrier integrity.</dd>

                        <dt>Forms & Bioactivity (α-TE):</dt>
                        <dd>There are eight natural forms of Vitamin E: four tocopherols (alpha, beta, gamma, delta) and four tocotrienols. However, the liver preferentially maintains <strong>alpha-tocopherol</strong> in the plasma via the alpha-tocopherol transfer protein (α-TTP). Therefore, Dietary Reference Intakes (DRIs) are based solely on alpha-tocopherol. Intake is often expressed as <strong>mg of alpha-Tocopherol Equivalents (α-TE)</strong> to account for the different activities of natural vs. synthetic alpha-tocopherol.</dd>

                        <dt>Absorption Factors:</dt>
                        <dd>As a fat-soluble vitamin, Vitamin E requires dietary fat and bile acids for proper absorption in the small intestine. Conditions that impair fat absorption (e.g., celiac disease, cystic fibrosis, chronic pancreatitis, cholestatic liver disease, bariatric surgery) can lead to Vitamin E deficiency.</dd>

                        <dt>Safety & UL:</dt>
                        <dd>The Tolerable Upper Intake Level (UL) for supplemental alpha-tocopherol (any form) for adults is <strong>1,000 mg/day</strong> (equivalent to approx. 1,500 IU of natural or 1,100 IU of synthetic Vitamin E). High doses above this level, and potentially even doses exceeding 300-400 mg/day, can increase the risk of bleeding (especially hemorrhagic stroke) by interfering with Vitamin K's blood clotting function. Caution is strongly advised when combining high-dose Vitamin E with anticoagulant medications (Warfarin, DOACs).</dd>
                    </dl>
                </div>

                {/* Content Block 2: Conversions (Simplified for α-TE) */}
                <div className="content-block">
                    <h3>2. Alpha-Tocopherol Equivalent (α-TE) Conversions</h3>
                    <p>Only alpha-tocopherol contributes to meeting the RDA. Natural and synthetic forms have different bioactivity:</p>
                    <table>
                        <thead>
                            <tr><th>Source Compound</th><th>Factor</th><th>Result</th></tr>
                        </thead>
                        <tbody>
                            <tr><td>1 mg RRR-α-tocopherol (Natural, often labeled d-alpha)</td><td>x 1</td><td>1 mg α-TE</td></tr>
                            <tr><td>1 mg all-rac-α-tocopherol (Synthetic, often labeled dl-alpha)</td><td>x 0.5</td><td>0.5 mg α-TE</td></tr>
                        </tbody>
                    </table>
                    <small>Other tocopherols (beta, gamma, delta) have antioxidant activity but do not significantly contribute to meeting Vitamin E requirements (RDA) because they are not effectively maintained by α-TTP. IU conversions are approx: 1 IU natural ≈ 0.67 mg α-TE; 1 IU synthetic ≈ 0.45 mg α-TE.</small>
                </div>

                {/* Content Block 3: RDAs */}
                <div className="content-block">
                    <h3>3. Reference Intakes (RDA / AI*)</h3>
                    <p>Recommended Dietary Allowances (RDA) or Adequate Intakes (AI) in mg α-TE per day.</p>
                    <table>
                        <thead>
                            <tr><th>Life Stage Group</th><th>Intake (mg α-TE/day)</th></tr>
                        </thead>
                        <tbody>
                            {/* Re-populate from vitERdaTable */}
                            <tr><td>Infants 0–6 months*</td><td>{vitERdaTable['0-6m'].target}</td></tr>
                            <tr><td>Infants 7–12 months*</td><td>{vitERdaTable['7-12m'].target}</td></tr>
                            <tr><td>Children 1–3 years</td><td>{vitERdaTable['1-3y'].target}</td></tr>
                            <tr><td>Children 4–8 years</td><td>{vitERdaTable['4-8y'].target}</td></tr>
                            <tr><td>Children 9–13 years</td><td>{vitERdaTable['9-13y'].target}</td></tr>
                            <tr><td>Adolescents & Adults 14+ years</td><td>{vitERdaTable['14+y'].target}</td></tr>
                            <tr><td>Pregnancy</td><td>{vitERdaTable['pregnant'].target}</td></tr>
                            <tr><td>Lactation</td><td>{vitERdaTable['lactating'].target}</td></tr>
                        </tbody>
                    </table>
                    <small>*Adequate Intake (AI)</small>
                </div>

                {/* Content Block 4: High Risk Groups */}
                <div className="content-block">
                    <h3>4. High-Risk / Special-Need Groups</h3>
                    <ul>
                        <li><strong>Low-Fat Diets / Poor Intake:</strong> Since Vitamin E is found primarily in fats (nuts, seeds, oils), very low-fat diets or poor overall intake can lead to inadequacy. Many adults do not meet the RDA.</li>
                        <li><strong>Fat Malabsorption Disorders:</strong> Conditions like celiac disease, cystic fibrosis, chronic pancreatitis, cholestasis, or history of bariatric surgery significantly impair Vitamin E absorption.</li>
                        <li><strong>Preterm Infants (very low birth weight):</strong> Born with limited stores, increasing risk of deficiency-related complications like hemolytic anemia.</li>
                        <li><strong>Increased Oxidative Stress:</strong> Individuals exposed to higher levels of oxidative stress (e.g., smokers, those consuming very high amounts of PUFAs, potentially heavy endurance athletes) might have increased antioxidant needs, though specific higher RDAs are not set.</li>
                        <li><strong>Anticoagulant Therapy Users:</strong> Individuals taking Warfarin (Coumadin) or DOACs (Eliquis, Xarelto, etc.) should avoid high-dose Vitamin E supplements (&gt; 300 mg/day is often cited as a caution threshold) due to increased bleeding risk.</li>
                        <li><strong>High Omega-3 Intake:</strong> Those taking high doses of fish oil (EPA/DHA) may need more Vitamin E to protect these fragile fats from oxidation. A common guideline is ~0.6 mg α-TE per gram of EPA+DHA, but consult a provider.</li>
                    </ul>
                </div>

                {/* Content Block 5: Food Sources */}
                <div className="content-block">
                    <h3>5. Key Food Sources (α-Tocopherol)</h3>
                    <p>Amounts are approximate mg of alpha-tocopherol per serving:</p>
                    <ul>
                        <li>Wheat Germ Oil (1 tbsp): ~20 mg</li>
                        <li>Sunflower Seeds (1 oz, ~2 tbsp): ~7.4 mg</li>
                        <li>Almonds (1 oz, ~23 nuts): ~7.3 mg</li>
                        <li>Sunflower Oil (1 tbsp): ~5.6 mg</li>
                        <li>Hazelnuts (1 oz): ~4.3 mg</li>
                        <li>Peanut Butter (2 tbsp): ~2.9 mg</li>
                        <li>Peanuts (1 oz): ~2.4 mg</li>
                        <li>Avocado (½ medium): ~2.1 mg</li>
                        <li>Olive Oil (1 tbsp): ~1.9 mg</li>
                        <li>Spinach (½ cup cooked): ~1.9 mg</li>
                        <li>Canola Oil (1 tbsp): ~1.8 mg</li>
                        <li>Broccoli (½ cup cooked): ~1.2 mg</li>
                        <li>Soybean Oil (1 tbsp): ~0.8 mg (Note: higher in gamma-tocopherol)</li>
                    </ul>
                    <small>Many vegetable oils like soybean and corn oil are richer in gamma-tocopherol, which has antioxidant benefits but doesn't count towards the Vitamin E RDA (α-TE).</small>
                </div>


                {/* --- Sources Section --- */}
                <div className="content-block">
                    <h3>Sources</h3>
                    <p className="sources"> {/* Ensure .sources class has appropriate styling */}
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin E Fact Sheet for Health Professionals*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminE-HealthProfessional/ <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin E Fact Sheet for Consumers*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminE-Consumer/ <br />
                        Institute of Medicine (US) Panel on Dietary Antioxidants and Related Compounds. (2000). *Dietary Reference Intakes for Vitamin C, Vitamin E, Selenium, and Carotenoids*. National Academies Press. https://doi.org/10.17226/9810 <br />
                        Niki, E. (2014). Role of vitamin E as a lipid-soluble peroxyl radical scavenger: In vitro and in vivo evidence. *Free Radical Biology and Medicine*, *66*, 3–12. https://doi.org/10.1016/j.freeradbiomed.2013.03.022 (Antioxidant Role - NCBI/PMC) <br />
                        Traber, M. G. (2007). Vitamin E regulatory mechanisms. *Annual Review of Nutrition*, *27*, 347–362. https://doi.org/10.1146/annurev.nutr.27.061406.093819 (Alpha-TTP - NCBI/PMC) <br />
                        Roberts, L. J., II, Oates, J. A., Linton, M. F., Fazio, S., Meador, B. P., & Lampkins, N. B. (2007). The relationship between dose of vitamin E and suppression of oxidative stress in humans. *Free Radical Biology & Medicine*, *43*(10), 1388–1393. https://doi.org/10.1016/j.freeradbiomed.2007.08.006 (Dose & Oxidative Stress - PMC) <br />
                        Kim, K., et al. (2013). Association between intake of vitamins and stroke mortality in Korean adults: a community-based prospective cohort study. *European Journal of Clinical Nutrition*, *67*(3), 266–273. (Haemorrhagic Stroke Risk - PMC) <br />
                        Wallert, M., et al. (2014). α-Tocopherol preserves endothelial function in apoE-deficient mice fed a high-fat diet containing ω-3 fatty acids. *Redox Biology*, *2*, 309–317. (Omega-3 Synergy - PMC) <br />
                        USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from https://fdc.nal.usda.gov/ (For food values) <br />
                        *Replace [April-14-2025] with the actual date you accessed the links.*
                    </p>
                </div>
                {/* --- END OF SOURCES SECTION --- */}

            </div> {/* End educational-section */}
        </div> // End vitaminECalculator
    );
}

export default VitaminECalculator;